import { useState } from "react";
import { useForm } from "react-hook-form";

import Button from "../../atomic/Button";
import Input from "../FormElements/Input";
import { ErrorTextBox } from "../../atomic/ErrorBox";
import { notify } from "../../../shared/context/AlertContext";
import SelectListBox from "../../atomic/Dropdown/SelectListBox";
import GetAuthContext from "../../../shared/context/AuthContext";
import { LoanApplicationService } from "../../../services/LoanApplication";
import { LOAN_CANCELLATION_REASONS } from "../../../services/LoanApplication/constants";
import {
  containsMarkupOrScript,
  getErrorText,
  REASON_TEXT_MAX_LENGTH,
  validateReasonText,
} from "../../../shared/utils/FormUtil";

interface IProps {
  setOpen: Function;
  reload: Function;
  loanApplicationID: string;
  pageName: string;
}

const COMMENT_VALIDATION_ERRORS = {
  comment: {
    required: "reason is required",
    hasOnlySpace: "reason cannot be only spaces",
    maxLength: `reason cannot exceed ${REASON_TEXT_MAX_LENGTH} characters`,
    noMarkup: "HTML or script tags are not allowed",
    allowedCharacters:
      "only letters, numbers, spaces and . , - _ ( ) / & ' : are allowed",
  },
};
export default function CancelLoanApplicationModal({
  setOpen,
  reload,
  loanApplicationID = "",
  pageName,
}: IProps) {
  const [reason, setReason] = useState<{ name: string }>({ name: "" });
  const {
    handleSubmit,
    setError,
    register,
    unregister,
    clearErrors,
    formState: { errors },
  } = useForm();
  const { fetchToken } = GetAuthContext();

  async function onSubmit(formState: any) {
    if (!reason?.name) {
      setError("reason", {
        message: "required",
      });
      return;
    }

    const isOtherReason = reason?.value === "other";
    const comment = formState?.comment?.trim() ?? "";

    // last gate before the payload is built, so an "other" reason can never be
    // submitted with markup or unsupported characters in it
    if (isOtherReason && !validateReasonText(comment)) {
      setError("comment", {
        type: containsMarkupOrScript(comment) ? "noMarkup" : "allowedCharacters",
      });
      return;
    }

    const payload = {
      loanApplicationID,
      token: fetchToken(),
      reason: isOtherReason ? comment : reason.name,
      pageName,
    };

    const response = await LoanApplicationService.cancelApplication(payload);

    if (response.status) {
      setOpen(false);
      reload();
    } else {
      notify({
        title: "Error",
        text: response.error,
      });
    }
  }

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset className="mb-4">
          <SelectListBox
            label="Reason"
            selected={reason}
            requiredIcon={true}
            options={LOAN_CANCELLATION_REASONS}
            setSelected={(data) => {
              clearErrors("reason");
              // drop the comment field (and any error on it) when the reason is
              // no longer "other", so a stale value cannot block the submit
              if (data?.value !== "other") unregister("comment");
              setReason(data);
            }}
            placeholder="Select Cancellation Reason"
            styles={{ options: "w-full" }}
          />
        </fieldset>
        {reason.value === "other" && (
          <fieldset className="mb-4">
            <Input
              register={register("comment", {
                required: true,
                maxLength: REASON_TEXT_MAX_LENGTH,
                validate: {
                  hasOnlySpace: (value) => Boolean(value?.trim()),
                  noMarkup: (value) => !containsMarkupOrScript(value ?? ""),
                  allowedCharacters: (value) => validateReasonText(value ?? ""),
                },
              })}
              type="text"
              label="Comment"
              requiredIcon={true}
              maxLength={REASON_TEXT_MAX_LENGTH}
              placeholder="Enter cancellation reason"
              error={errors.comment}
              errorText={getErrorText(
                "comment",
                errors,
                COMMENT_VALIDATION_ERRORS
              )}
            />
          </fieldset>
        )}
        {errors.reason ? (
          <ErrorTextBox text={errors.reason.message}></ErrorTextBox>
        ) : null}
        <fieldset className="flex items-center justify-end mt-4 mb-1">
          <Button
            type="submit"
            color="primary"
            disabled={!Boolean(reason?.value)}
            className="text-sm px-4 py-2 w-auto rounded-lg text-white flex items-center justify-center font-medium"
          >
            Confirm
          </Button>
        </fieldset>
      </form>
    </div>
  );
}
